import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Alert, Typography, Button } from '@mui/material';
import { getConfig } from '../config/environment';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const config = getConfig();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check console_access property first (new RBAC system)
  const consoleAccess = (user as any)?.console_access || [];
  const hasConsoleAccess = consoleAccess.includes('chat');
  
  // Fallback to permission-based check for backward compatibility
  const hasChatAccess = hasConsoleAccess ||
                        user.permissions?.includes('chat') || 
                        user.role === 'admin' || 
                        user.is_admin;

  if (!hasChatAccess) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ p: 4 }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography paragraph>
            You don't have permission to access the Chat application. 
            Please contact your administrator to request access.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your current permissions: {user.permissions?.join(', ') || 'None'}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            {user.permissions?.includes('admin') && (
              <Button 
                variant="contained" 
                href={config.adminUrl}
              >
                Go to Admin Console
              </Button>
            )}
            {user.permissions?.includes('tickets') && (
              <Button 
                variant="contained" 
                href={config.ticketsUrl}
              >
                Go to Tickets App
              </Button>
            )}
          </Box>
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
